import React, { useState, useEffect } from "react";
import "../styles/App.css";
import "../styles/custom_form.css";
import "../styles/FormBuilderModal.css";

const FormBuilderModal = ({ formType, onSave, onClose }) => {
  // Function to get default fields based on form type
  const getDefaultFields = () => {
    // Base fields for both form types
    const baseFields = [
      {
        id: 1,
        label: "Full Name",
        type: "text",
        required: true,
        placeholder: "Enter your full name",
      },
      {
        id: 2,
        label: "Email Address",
        type: "email",
        required: true,
        placeholder: "Enter your email address",
      },
    ];

    // For SPEAKER form - Add talk-specific fields
    if (formType === "speaker") {
      return [
        ...baseFields,
        {
          id: 3,
          label: "Talk Topic",
          type: "text",
          required: true,
          placeholder: "Enter your talk topic",
        },
        {
          id: 4,
          label: "Talk Details",
          type: "textarea",
          required: true,
          placeholder: "Enter your abstract or speaker bio",
        },
        {
          id: 5,
          label: "Phone Number",
          type: "tel",
          required: false,
          placeholder: "Enter your phone number",
        },
        {
          id: 6,
          label: "Organization",
          type: "text",
          required: false,
          placeholder: "Enter your organization name",
        },
      ];
    }

    // For ATTENDEE form - Different fields
    return [
      ...baseFields,
      {
        id: 3,
        label: "Phone Number",
        type: "tel",
        required: true,
        placeholder: "Enter your phone number",
      },
      {
        id: 4,
        label: "Organization",
        type: "text",
        required: false,
        placeholder: "Enter your organization name",
      },
    ];
  };

  const defaultFields = getDefaultFields();

  const [formFields, setFormFields] = useState(defaultFields);
  const [newField, setNewField] = useState({
    label: "",
    type: "text",
    required: false,
    placeholder: "",
    options: [],
  });
  const [formData, setFormData] = useState({});
  const [isEditing, setIsEditing] = useState(null);
  const [newOption, setNewOption] = useState("");
  const [generatedCode, setGeneratedCode] = useState("");
  const [activeTab, setActiveTab] = useState("builder");

  // Load saved schema on mount - check for form type specific schema
  useEffect(() => {
    try {
      const saved = localStorage.getItem(
        `eventfork_registration_schema_${formType}`,
      );
      if (saved) {
        const parsed = JSON.parse(saved);

        // For speaker form, ensure we have talk fields
        if (formType === "speaker") {
          const hasTalkTopic = parsed.some(
            field => field.label === "Talk Topic"
          );

          const hasTalkDetail = parsed.some(
            field => field.label === "Talk Detail"
          );

          // If saved schema is missing talk fields, use default fields instead
          if (!hasTalkTopic || !hasTalkDetail) {
            console.log('Saved schema missing talk fields, loading defaults');
            setFormFields(defaultFields);
            return;
          }
        }

        if (Array.isArray(parsed) && parsed.length > 0) {
          setFormFields(parsed);
          return;
        }
      }

      // If no saved schema, use default fields
      setFormFields(defaultFields);
    } catch (e) {
      console.error("Failed to load saved registration schema", e);
      setFormFields(defaultFields);
    }
  }, [formType]);

  // Field types available
  const fieldTypes = [
    { value: "text", label: "Text Input" },
    { value: "email", label: "Email Input" },
    { value: "tel", label: "Phone Number" },
    { value: "number", label: "Number Input" },
    { value: "textarea", label: "Text Area" },
    { value: "select", label: "Dropdown" },
    { value: "radio", label: "Radio Buttons" },
    { value: "checkbox", label: "Checkbox" },
    { value: "date", label: "Date Picker" },
    { value: "time", label: "Time Picker" },
  ];

  // Handle input change in live preview
  const handleInputChange = (fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  // Add a new field
  const addField = () => {
    if (!newField.label.trim()) {
      alert("Field label is required");
      return;
    }

    if (
      (newField.type === "select" || newField.type === "radio") &&
      newField.options.length === 0
    ) {
      alert("Please add at least one option for dropdown/radio fields");
      return;
    }

    const fieldId = Date.now();
    const field = {
      id: fieldId,
      label: newField.label,
      type: newField.type,
      required: newField.required,
      placeholder: newField.placeholder,
      options: [...newField.options],
    };

    setFormFields([...formFields, field]);
    resetNewField();
  };


  // Check if Edit/Delete should be hidden for this field
  const isProtectedField = (field) => {
    if (!field || !field.label) {
      return false;
    }

    const label = field.label.trim().toLowerCase();

    // Locked in both Attendee and Speaker forms
    if (
      label === "full name" ||
      label === "email address"
    ) {
      return true;
    }

    // Locked only in Speaker form
    if (
      formType === "speaker" &&
      (
        label === "talk topic" ||
        label === "talk detail" ||
        label === "talk details"
      )
    ) {
      return true;
    }

    return false;
  };
  // Edit existing field
  const editField = (fieldId) => {
    console.log("Edit clicked for fieldId:", fieldId);
    console.log("Current formFields:", formFields);

    // Make sure we're comparing numbers correctly
    const field = formFields.find(
      (f) => Number(f.id) === Number(fieldId)
    );

    console.log("Found field:", field);

    if (!field) {
      console.error("Field not found with id:", fieldId);
      alert("Error: Could not find field to edit");
      return;
    }

    // Check if this field is protected
    if (isProtectedField(field)) {
      console.log("This field is protected and cannot be edited:", field.label);
      return;
    }

    // Create a clean copy of the field with all properties
    const fieldCopy = {
      label: field.label || "",
      type: field.type || "text",
      required: field.required || false,
      placeholder: field.placeholder || "",
      options: field.options ? [...field.options] : []
    };

    console.log("Setting newField to:", fieldCopy);

    setNewField(fieldCopy);
    setIsEditing(fieldId);

    // Scroll to the top of the form builder panel
    setTimeout(() => {
      const fieldForm = document.querySelector(".field-form");
      const builderPanel = document.querySelector(".builder-panel-modal");

      if (fieldForm) {
        fieldForm.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest"
        });

        // Add highlight effect
        fieldForm.classList.add("highlight-pulse");

        setTimeout(() => {
          fieldForm.classList.remove("highlight-pulse");
        }, 1000);

      } else if (builderPanel) {
        builderPanel.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      }
    }, 100);
  };

  // Update field
  const updateField = () => {
    console.log("Updating field with id:", isEditing);
    console.log("New field data:", newField);

    if (!newField.label.trim()) {
      alert("Field label is required");
      return;
    }

    if ((newField.type === "select" || newField.type === "radio") && newField.options.length === 0) {
      alert("Please add at least one option for dropdown/radio fields");
      return;
    }

    // Update the form fields
    const updatedFormFields = formFields.map((field) => {
      if (Number(field.id) === Number(isEditing)) {
        return {
          ...field,
          label: newField.label,
          type: newField.type,
          required: newField.required,
          placeholder: newField.placeholder || "",
          options: newField.options ? [...newField.options] : []
        };
      }
      return field;
    });

    console.log("Updated form fields:", updatedFormFields);

    setFormFields(updatedFormFields);
    resetNewField();
    setIsEditing(null);
  };

  // Remove field
  const removeField = (fieldId) => {
    setFormFields((prev) => prev.filter((field) => field.id !== fieldId));
    setFormData((prev) => {
      const newData = { ...prev };
      delete newData[fieldId];
      return newData;
    });
  };

  // Move field up/down
  const moveField = (index, direction) => {
    const newFields = [...formFields];
    if (direction === "up" && index > 0) {
      [newFields[index], newFields[index - 1]] = [
        newFields[index - 1],
        newFields[index],
      ];
    } else if (direction === "down" && index < newFields.length - 1) {
      [newFields[index], newFields[index + 1]] = [
        newFields[index + 1],
        newFields[index],
      ];
    }
    setFormFields(newFields);
  };

  // Add option to dropdown/radio field
  const addOption = () => {
    if (!newOption.trim()) return;
    setNewField((prev) => ({
      ...prev,
      options: [...prev.options, newOption.trim()],
    }));
    setNewOption("");
  };

  // Remove option
  const removeOption = (optionIndex) => {
    setNewField((prev) => ({
      ...prev,
      options: prev.options.filter((_, idx) => idx !== optionIndex),
    }));
  };

  // Reset new field form
  const resetNewField = () => {
    setNewField({
      label: "",
      type: "text",
      required: false,
      placeholder: "",
      options: [],
    });
    setNewOption("");
  };

  // Cancel editing
  const cancelEdit = () => {
    resetNewField();
    setIsEditing(null);
  };

  // Reset to default fields
  const resetToDefaultFields = () => {
    if (window.confirm('Reset all fields to default? This will remove all custom fields you\'ve added.')) {
      setFormFields(defaultFields);
      localStorage.removeItem(`eventfork_registration_schema_${formType}`);
      setFormData({});
    }
  };

  // Generate React code for the form
  const generateCode = () => {
    const fieldsCode = formFields.map(field => {
      const fieldName = field.label.toLowerCase().replace(/\s+/g, '_');

      switch (field.type) {
        case 'textarea':
          return `  <div className="form-group">
    <label htmlFor="${fieldName}">${field.label}${field.required ? ' *' : ''}</label>
    <textarea
      id="${fieldName}"
      name="${fieldName}"
      value={formData.${fieldName}}
      onChange={(e) => handleChange('${fieldName}', e.target.value)}
      placeholder="${field.placeholder}"
      required={${field.required}}
      rows="4"
    />
  </div>`;

        case 'select':
          const optionsCode = field.options.map(option => `      <option value="${option}">${option}</option>`).join('\n');
          return `  <div className="form-group">
    <label htmlFor="${fieldName}">${field.label}${field.required ? ' *' : ''}</label>
    <select
      id="${fieldName}"
      name="${fieldName}"
      value={formData.${fieldName}}
      onChange={(e) => handleChange('${fieldName}', e.target.value)}
      required={${field.required}}
    >
      <option value="">Select ${field.label.toLowerCase()}</option>
${optionsCode}
    </select>
  </div>`;

        case 'radio':
          const radioOptions = field.options.map(option => `      <label className="radio-option">
        <input
          type="radio"
          name="${fieldName}"
          value="${option}"
          checked={formData.${fieldName} === '${option}'}
          onChange={(e) => handleChange('${fieldName}', e.target.value)}
        />
        ${option}
      </label>`).join('\n');
          return `  <div className="form-group">
    <label>${field.label}${field.required ? ' *' : ''}</label>
    <div className="radio-group">
${radioOptions}
    </div>
  </div>`;

        case 'checkbox':
          return `  <div className="form-group">
    <label className="checkbox-label">
      <input
        type="checkbox"
        name="${fieldName}"
        checked={formData.${fieldName}}
        onChange={(e) => handleChange('${fieldName}', e.target.checked)}
      />
      ${field.label}
    </label>
  </div>`;

        default:
          return `  <div className="form-group">
    <label htmlFor="${fieldName}">${field.label}${field.required ? ' *' : ''}</label>
    <input
      type="${field.type}"
      id="${fieldName}"
      name="${fieldName}"
      value={formData.${fieldName}}
      onChange={(e) => handleChange('${fieldName}', e.target.value)}
      placeholder="${field.placeholder}"
      required={${field.required}}
    />
  </div>`;
      }
    }).join('\n\n');

    const code = `import React, { useState } from 'react';
import './EventForm.css';

const EventForm = () => {
  const [formData, setFormData] = useState({
${formFields.map(field => `    ${field.label.toLowerCase().replace(/\s+/g, '_')}: ''`).join(',\n')}
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Add your submission logic here
  };

  return (
    <div className="form-container">
      <h2>Event Registration</h2>
      <form onSubmit={handleSubmit}>
${fieldsCode}
        
        <div className="form-actions">
          <button type="submit" className="submit-btn">
            Register
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventForm;`;

    setGeneratedCode(code);
    setActiveTab('code');
  };

  // Map FormBuilder field types to API field types
  const getFieldTypeMapping = (type) => {
    const mapping = {
      text: "TextInput",
      email: "EmailInput",
      tel: "PhoneInput",
      number: "NumberInput",
      textarea: "TextArea",
      select: "Dropdown",
      radio: "RadioGroup",
      checkbox: "Checkbox",
      date: "DatePicker",
      time: "TimePicker",
    };
    return mapping[type] || "TextInput";
  };

  // Handle save
  const handleSave = () => {
    try {
      // Transform form fields to API format
      const customFormFields = formFields.map((field) => ({
        label: field.label,
        field_type: getFieldTypeMapping(field.type),
        is_required: field.required || false,
        place_holder: field.placeholder || "",
        options:
          field.type === "select" || field.type === "radio"
            ? field.options
            : [],
      }));

      // Save schema for this form type
      localStorage.setItem(
        `eventfork_registration_schema_${formType}`,
        JSON.stringify(formFields),
      );

      // Save custom form data separately
      localStorage.setItem(
        `eventfork_custom_form_data_${formType}`,
        JSON.stringify({
          type: formType,
          fields: customFormFields,
          timestamp: new Date().toISOString()
        }),
      );

      // Call the onSave callback with the custom form fields
      onSave(formType, customFormFields);

      // Close the modal
      onClose();
    } catch (e) {
      console.error("Failed to save custom form configuration", e);
      alert("Failed to save form configuration. Please try again.");
    }
  };

  // Get form type display name
  const getFormTypeDisplayName = () => {
    return formType === "speaker"
      ? "Speaker Registration"
      : "Attendee Registration";
  };

  // Handle form submission in preview
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form Data:", formData);
    alert("Form submitted! Check console for data.");
  };

  // Handle modal close with escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div className="form-builder-modal-overlay" onClick={onClose}>
      <div className="form-builder-modal" onClick={(e) => e.stopPropagation()}>
        <div className="form-builder-modal-header">
         <h2>
  📝 Custom Form Builder - {getFormTypeDisplayName()}
  <span className="form-change-note">
    [Once the event is created, this form cannot be modified]
  </span>
</h2>
          <button
            type="button"
            className="close-modal-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="form-builder-modal-tabs">
          <button
            className={`modal-tab-btn ${activeTab === "builder" ? "active" : ""}`}
            onClick={() => setActiveTab("builder")}
          >
            Form Builder
          </button>

        </div>

        <div className="form-builder-modal-content">
          <div className="builder-layout-modal">
            {/* Left Panel - Field Builder */}
            <div className="builder-panel-modal">
              <div className="panel-header-modal">
                <h3>Custom Form Builder</h3>
                <div className="panel-header-actions">
                  <button
                    type="button"
                    className="reset-default-btn"
                    onClick={resetToDefaultFields}
                    title="Reset to default fields"
                  >
                    <i className="fas fa-undo-alt" /> Reset to Default
                  </button>
                  <div className="form-type-badge">
                    <i
                      className={`fas ${formType === "speaker" ? "fa-microphone" : "fa-users"}`}
                    />
                    {getFormTypeDisplayName()}
                  </div>
                </div>
              </div>

              {activeTab === "builder" ? (
                <>
                  <div className="field-form">
                    <h4>{isEditing ? "Edit Field" : "Add New Field"}</h4>

                    <div className="form-group">
                      <label>Field Label *</label>
                      <input
                        type="text"
                        value={newField.label}
                        onChange={(e) =>
                          setNewField({ ...newField, label: e.target.value })
                        }
                        placeholder="e.g., Job Title, Company Size, etc."
                      />
                    </div>

                    <div className="form-group">
                      <label>Field Type</label>
                      <select
                        value={newField.type}
                        onChange={(e) =>
                          setNewField({
                            ...newField,
                            type: e.target.value,
                            options:
                              e.target.value === "select" ||
                                e.target.value === "radio"
                                ? newField.options
                                : [],
                          })
                        }
                      >
                        {fieldTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Placeholder Text</label>
                      <input
                        type="text"
                        value={newField.placeholder}
                        onChange={(e) =>
                          setNewField({
                            ...newField,
                            placeholder: e.target.value,
                          })
                        }
                        placeholder="Optional placeholder text"
                      />
                    </div>

                    {(newField.type === "select" ||
                      newField.type === "radio") && (
                        <div className="form-group">
                          <label>Options</label>
                          <div className="options-container">
                            <div className="option-input">
                              <input
                                type="text"
                                value={newOption}
                                onChange={(e) => setNewOption(e.target.value)}
                                placeholder="Add an option"
                                onKeyPress={(e) =>
                                  e.key === "Enter" && addOption()
                                }
                              />
                              <button
                                onClick={addOption}
                                className="add-option-btn"
                              >
                                Add
                              </button>
                            </div>
                            <div className="options-list">
                              {newField.options.map((option, index) => (
                                <div key={index} className="option-item">
                                  <span>{option}</span>
                                  <button
                                    onClick={() => removeOption(index)}
                                    className="remove-option-btn"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={newField.required}
                          onChange={(e) =>
                            setNewField({
                              ...newField,
                              required: e.target.checked,
                            })
                          }
                        />
                        Required Field
                      </label>
                    </div>

                    <div className="form-actions">
                      {isEditing ? (
                        <>
                          <button onClick={updateField} className="primary-btn">
                            Update Field
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="secondary-btn"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button onClick={addField} className="primary-btn">
                          Add Field
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Existing Fields List */}
                  <div className="fields-list">
                    <h4>Form Fields ({formFields.length})</h4>
                    {formFields.map((field, index) => (
                      <div key={field.id} className="field-item">
                        <div className="field-info">
                          <span className="field-label">{field.label}</span>
                          <span className="field-type">
                            {
                              fieldTypes.find((t) => t.value === field.type)
                                ?.label
                            }
                          </span>
                          {field.required && (
                            <span className="required-badge">Required</span>
                          )}
                        </div>
                        <div className="field-actions">
                          <button
                            onClick={() => moveField(index, "up")}
                            disabled={index === 0}
                            className="move-btn"
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => moveField(index, "down")}
                            disabled={index === formFields.length - 1}
                            className="move-btn"
                            title="Move down"
                          >
                            ↓
                          </button>

                          {!isProtectedField(field) && (
                            <button
                              onClick={() => editField(field.id)}
                              className="edit-btn"
                              title="Edit"
                            >
                              ✎
                            </button>
                          )}


                          {!isProtectedField(field) && (
                            <button
                              onClick={() => removeField(field.id)}
                              className="delete-btn"
                              title="Delete"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="code-preview">
                  <div className="code-header">
                    <h4>Generated React Code</h4>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(generatedCode);
                        alert("Code copied to clipboard!");
                      }}
                      className="copy-btn"
                    >
                      Copy Code
                    </button>
                  </div>
                  <pre className="code-block">
                    <code>{generatedCode}</code>
                  </pre>
                </div>
              )}
            </div>

            {/* Right Panel - Live Preview */}
            <div className="preview-panel-modal">
              <div className="panel-header-modal">
                <h3>Live Preview - {getFormTypeDisplayName()} Form</h3>
                <span className="preview-subtitle">Real-time form preview</span>
              </div>

              <div className="preview-container">
                <form onSubmit={handleSubmit} className="event-form">
                  <h4>🎟️ {getFormTypeDisplayName()} Form</h4>
                  <p className="form-description">
                    Please fill out all required fields to{" "}
                    {formType === "speaker"
                      ? "submit your speaker proposal"
                      : "register for the event"}
                    .
                  </p>

                  {formFields.map((field) => (
                    <div key={field.id} className="form-group">
                      <label htmlFor={`field-${field.id}`}>
                        {field.label}
                        {field.required && (
                          <span className="required-star"> *</span>
                        )}
                      </label>

                      {field.type === "textarea" ? (
                        <textarea
                          id={`field-${field.id}`}
                          value={formData[field.id] || ""}
                          onChange={(e) =>
                            handleInputChange(field.id, e.target.value)
                          }
                          placeholder={field.placeholder}
                          required={field.required}
                          rows="4"
                        />
                      ) : field.type === "select" ? (
                        <select
                          id={`field-${field.id}`}
                          value={formData[field.id] || ""}
                          onChange={(e) =>
                            handleInputChange(field.id, e.target.value)
                          }
                          required={field.required}
                        >
                          <option value="">
                            Select {field.label.toLowerCase()}
                          </option>
                          {field.options.map((option, index) => (
                            <option key={index} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : field.type === "radio" ? (
                        <div className="radio-group">
                          {field.options.map((option, index) => (
                            <label key={index} className="radio-option">
                              <input
                                type="radio"
                                name={`field-${field.id}`}
                                value={option}
                                checked={formData[field.id] === option}
                                onChange={(e) =>
                                  handleInputChange(field.id, e.target.value)
                                }
                                required={field.required}
                              />
                              {option}
                            </label>
                          ))}
                        </div>
                      ) : field.type === "checkbox" ? (
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData[field.id] || false}
                            onChange={(e) =>
                              handleInputChange(field.id, e.target.checked)
                            }
                          />
                          {field.label}
                        </label>
                      ) : (
                        <input
                          type={field.type}
                          id={`field-${field.id}`}
                          value={formData[field.id] || ""}
                          onChange={(e) =>
                            handleInputChange(field.id, e.target.value)
                          }
                          placeholder={field.placeholder}
                          required={field.required}
                        />
                      )}
                    </div>
                  ))}

                  <div className="form-actions">
                    <button type="submit" className="submit-btn_custom" disabled>
                      {formType === "speaker"
                        ? "Submit Proposal"
                        : "Register Now"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="form-builder-modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
          >
            <i className="fas fa-save" /> Save Custom Form
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormBuilderModal;